/**
 * ToolCallCard — renders one tool call inside an assistant message.
 *
 * The brief specifically called this out as the highest-leverage UX move
 * for the chat interface. Users seeing "Querying Athena... 4s" with a
 * spinner is dramatically better than dead-air silence while Athena does
 * its 5-30s thing.
 */
import { useEffect, useState } from "react";
import type { ToolCall } from "./useChatStream";

interface Props {
  tool: ToolCall;
}

export function ToolCallCard({ tool }: Props) {
  const [elapsed, setElapsed] = useState(0);

  // Tick every 100ms while running so the timer animates.
  useEffect(() => {
    if (tool.status !== "running") return;
    const interval = setInterval(() => {
      setElapsed((Date.now() - tool.startedAt) / 1000);
    }, 100);
    return () => clearInterval(interval);
  }, [tool.status, tool.startedAt]);

  const finalElapsed =
    tool.finishedAt && tool.startedAt
      ? ((tool.finishedAt - tool.startedAt) / 1000).toFixed(1)
      : null;

  const label = labelForTool(tool.name);
  const description = descriptionForTool(tool.name);

  const isRunning = tool.status === "running";
  const isError = tool.status === "error";

  return (
    <div
      className="my-3 rounded-md border hairline px-3.5 py-2.5 t-base"
      style={{
        background: isError ? "var(--oxblood-bg)" : "var(--bg-elev)",
        borderColor: isError ? "var(--oxblood)" : "var(--line)",
      }}
    >
      <div className="flex items-center gap-2.5">
        {/* Status icon */}
        <div
          className={`h-2 w-2 rounded-full shrink-0 ${isRunning ? "tool-pulse" : ""}`}
          style={{
            background: isError ? "var(--oxblood)" : isRunning ? "var(--brass)" : "var(--forest)",
          }}
        />

        {/* Tool name + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className="text-[13px] font-medium"
              style={{ color: "var(--ink)" }}
            >
              {label}
            </span>
            <span
              className="text-[11px] font-mono uppercase tracking-wider"
              style={{ color: "var(--ink-3)" }}
            >
              {description}
            </span>
          </div>

          {/* Summary (when finished) */}
          {!isRunning && tool.summary && (
            <div
              className="text-[12.5px] mt-0.5 font-serif italic"
              style={{ color: isError ? "var(--oxblood)" : "var(--ink-2)" }}
            >
              {tool.summary}
            </div>
          )}
        </div>

        {/* Elapsed time */}
        <div
          className="text-[11px] font-mono tabular-nums shrink-0"
          style={{ color: "var(--ink-3)" }}
        >
          {isRunning ? `${elapsed.toFixed(1)}s` : finalElapsed ? `${finalElapsed}s` : ""}
        </div>
      </div>
    </div>
  );
}

function labelForTool(name: string): string {
  switch (name) {
    case "query_athena":
      return "Querying Athena";
    default:
      return name;
  }
}

function descriptionForTool(name: string): string {
  switch (name) {
    case "query_athena":
      return "ashi_prod";
    default:
      return "tool";
  }
}
